// Battle screen - turn-based combat on 7x10 grid

import { Canvas } from '../Canvas';
import { CONSTANTS, ScreenState } from '../types';
import { Screen, ScreenContext } from './Screen';
import { GameState, getCurrentTeam } from '../systems/GameState';
import { modifyMoney } from '../entities/Team';
import { GAME_CONSTANTS } from '../data/constants';
import {
  GladiatorData, isAlive, getMeleeWeapon, getRangedWeapon,
  getMeleeDmg, getRangedDmg, getArmorValue, getAtkSkillForWeapon, getDefSkillForWeapon,
  getKnownSpells, getAtkSkillForSpell, isStunned, isRooted, tickEffects,
  addActiveEffect, clearActiveEffects, getProtectionBonus, getHitChanceModifier
} from '../entities/Gladiator';
import { Spell, SPELL_TYPE, isFriendlySpell } from '../data/spells';
import { rollDiceStr, random } from '../utils/dice';
import { AnimationSystem, HitMarkerType } from '../systems/AnimationSystem';
import { getAnimationSpeedMultiplier } from './Settings';

// AI turn delay base (modified by animation speed)
const AI_TURN_DELAY_BASE = 300;
const AI_SPECTATOR_DELAY_BASE = 80; // Faster for spectator mode

// Direction deltas for 8-directional movement
const DIRECTIONS: Record<string, { dx: number; dy: number }> = {
  NW: { dx: -1, dy: -1 },
  N: { dx: 0, dy: -1 },
  NE: { dx: 1, dy: -1 },
  E: { dx: 1, dy: 0 },
  SE: { dx: 1, dy: 1 },
  S: { dx: 0, dy: 1 },
  SW: { dx: -1, dy: 1 },
  W: { dx: -1, dy: 0 },
};

const DIR_ENTRIES = Object.entries(DIRECTIONS);

interface BattleState {
  round: number;
  turnOrder: string[]; // gladiator IDs
  currentTurnIndex: number;
  mapTiles: (string | null)[][]; // gladiator ID at each position
  clipMask: boolean[][]; // true = blocked tile
  bottomTeamId: string;
  topTeamId: string;
  battleEnded: boolean;
  winningTeamId: string | null;
  messages: string[];
}

type TargetingMode = 'none' | 'ranged' | 'spell';

// Message log constants
const MESSAGE_LOG_MAX_BUFFER = 100;
const MESSAGE_LOG_VISIBLE_LINES = 5;
const MESSAGE_LOG_LINE_HEIGHT = 18;

export class Battle implements Screen {
  readonly state = ScreenState.BATTLE;
  private context: ScreenContext;
  private gameState: GameState | null = null;
  private battleState: BattleState | null = null;
  private targetingMode: TargetingMode = 'none';
  private selectedSpell: Spell | null = null;
  private validTargets: string[] = [];
  private animationSystem: AnimationSystem;
  private messageScrollOffset: number = 0; // 0 = bottom (newest), positive = scrolled up
  private spectatorMode: boolean = false; // true when watching AI vs AI battle
  private aiTurnInProgress: boolean = false; // prevents multiple AI actions being queued

  constructor(context: ScreenContext) {
    this.context = context;
    this.animationSystem = new AnimationSystem();
  }

  // Set spectator mode (watching AI vs AI battle)
  setSpectatorMode(enabled: boolean): void {
    this.spectatorMode = enabled;
  }

  // Initialize a spectator battle (AI vs AI)
  initializeSpectatorBattle(): void {
    if (!this.gameState) return;

    this.spectatorMode = true;

    // Find two AI teams to battle
    const aiTeams = Array.from(this.gameState.teams.values())
      .filter(t => !this.gameState!.playerTeamIds.includes(t.id) && t.gladiatorIds.length >= 3);

    if (aiTeams.length < 2) {
      console.log('Not enough AI teams for spectator battle');
      this.spectatorMode = false;
      return;
    }

    // Pick two random AI teams
    const team1 = aiTeams[Math.floor(Math.random() * aiTeams.length)]!;
    const remainingTeams = aiTeams.filter(t => t.id !== team1.id);
    const team2 = remainingTeams[Math.floor(Math.random() * remainingTeams.length)]!;

    // Set up battle with these two teams (using team1 as "bottom" and team2 as "top")
    this.initializeBattleBetweenTeams(team1, team2);
  }

  // Auto-fill a team's battle roster with healthy gladiators
  private autoFillBattleRoster(team: any): void {
    if (!this.gameState) return;

    team.battleGladiatorIds = [null, null, null, null, null, null];
    let slot = 0;
    for (const gId of team.gladiatorIds) {
      if (slot >= GAME_CONSTANTS.MAX_GLADIATORS_IN_BATTLE) break;
      const g = this.gameState.gladiators.get(gId);
      if (g && g.hurt === 0) {
        team.battleGladiatorIds[slot] = gId;
        slot++;
      }
    }
  }

  // Initialize a battle between two specific teams
  private initializeBattleBetweenTeams(team1: any, team2: any): void {
    if (!this.gameState) return;

    this.autoFillBattleRoster(team1);
    this.autoFillBattleRoster(team2);

    // Initialize map tiles and clip mask
    const mapTiles: (string | null)[][] = [];
    const clipMask: boolean[][] = [];
    for (let x = 0; x < GAME_CONSTANTS.MAP_WIDTH; x++) {
      mapTiles[x] = [];
      clipMask[x] = [];
      for (let y = 0; y < GAME_CONSTANTS.MAP_HEIGHT; y++) {
        mapTiles[x][y] = null;
        clipMask[x][y] = false;
      }
    }

    this.parseClipMap(clipMask);

    const turnOrder: string[] = [];

    // Bottom team - rows 8-9
    const bottomTeamGladiators: string[] = [];
    for (let i = 0; i < GAME_CONSTANTS.MAX_GLADIATORS_IN_BATTLE; i++) {
      const gId = team1.battleGladiatorIds[i];
      if (!gId) continue;

      const gladiator = this.gameState.gladiators.get(gId);
      if (!gladiator) continue;

      const x = 2 + (i % 3);
      const y = 8 + Math.floor(i / 3);

      gladiator.position = { x, y };
      gladiator.hp = gladiator.maxHp;
      gladiator.mana = gladiator.maxMana;
      gladiator.ammo = GAME_CONSTANTS.AMMO_COUNT;
      gladiator.needsReload = false;
      mapTiles[x]![y] = gId;
      bottomTeamGladiators.push(gId);
    }

    // Top team - rows 0-1
    const topTeamGladiators: string[] = [];
    for (let i = 0; i < GAME_CONSTANTS.MAX_GLADIATORS_IN_BATTLE; i++) {
      const gId = team2.battleGladiatorIds[i];
      if (!gId) continue;

      const gladiator = this.gameState.gladiators.get(gId);
      if (!gladiator) continue;

      const x = 2 + (i % 3);
      const y = Math.floor(i / 3);

      gladiator.position = { x, y };
      gladiator.hp = gladiator.maxHp;
      gladiator.mana = gladiator.maxMana;
      gladiator.ammo = GAME_CONSTANTS.AMMO_COUNT;
      gladiator.needsReload = false;
      mapTiles[x]![y] = gId;
      topTeamGladiators.push(gId);
    }

    // Interleave turn order
    for (let i = 0; i < Math.max(bottomTeamGladiators.length, topTeamGladiators.length); i++) {
      if (bottomTeamGladiators[i]) turnOrder.push(bottomTeamGladiators[i]!);
      if (topTeamGladiators[i]) turnOrder.push(topTeamGladiators[i]!);
    }

    this.battleState = {
      round: 1,
      turnOrder,
      currentTurnIndex: 0,
      mapTiles,
      clipMask,
      bottomTeamId: team1.id,
      topTeamId: team2.id,
      battleEnded: false,
      winningTeamId: null,
      messages: [`${team1.name} vs ${team2.name} - Taistelu alkaa!`],
    };
  }

  // Get AI turn delay adjusted by animation speed
  private getAIDelay(): number {
    const speedMultiplier = getAnimationSpeedMultiplier();
    const baseDelay = this.spectatorMode ? AI_SPECTATOR_DELAY_BASE : AI_TURN_DELAY_BASE;
    return Math.round(baseDelay / speedMultiplier);
  }

  setGameState(gameState: GameState): void {
    this.gameState = gameState;
  }

  enter(): void {
    console.log('Entering Battle');
    this.messageScrollOffset = 0;
    // Reset spectator mode (will be set by caller if needed)
    this.spectatorMode = false;
    this.aiTurnInProgress = false;
    this.animationSystem.setHitMarkerAssets(this.context.assets.hitMarkers);
    this.animationSystem.clearAll();
    this.initializeBattle();
  }

  exit(): void {
    console.log('Exiting Battle');
    this.battleState = null;
    this.targetingMode = 'none';
    this.selectedSpell = null;
    this.validTargets = [];
    this.aiTurnInProgress = false;
    this.animationSystem.clearAll();
  }

  private initializeBattle(): void {
    if (!this.gameState) return;

    const playerTeam = getCurrentTeam(this.gameState);
    if (!playerTeam) return;

    // For now, create a dummy AI opponent from another team
    // In a real game, this would be determined by the season schedule
    let opponentTeam = null;
    for (const [id, team] of this.gameState.teams) {
      if (id !== playerTeam.id && team.gladiatorIds.length > 0) {
        opponentTeam = team;
        break;
      }
    }

    if (!opponentTeam) {
      console.log('No opponent found');
      return;
    }

    // Auto-fill opponent's battle team
    this.autoFillBattleRoster(opponentTeam);

    // Initialize map tiles
    const mapTiles: (string | null)[][] = [];
    const clipMask: boolean[][] = [];
    for (let x = 0; x < GAME_CONSTANTS.MAP_WIDTH; x++) {
      mapTiles[x] = [];
      clipMask[x] = [];
      for (let y = 0; y < GAME_CONSTANTS.MAP_HEIGHT; y++) {
        mapTiles[x][y] = null;
        clipMask[x][y] = false;
      }
    }

    // Parse clip map from asset
    this.parseClipMap(clipMask);

    // Place gladiators
    const turnOrder: string[] = [];

    // Bottom team (player) - rows 8-9
    const bottomTeamGladiators: string[] = [];
    for (let i = 0; i < GAME_CONSTANTS.MAX_GLADIATORS_IN_BATTLE; i++) {
      const gId = playerTeam.battleGladiatorIds[i];
      if (!gId) continue;

      const gladiator = this.gameState.gladiators.get(gId);
      if (!gladiator) continue;

      const x = 2 + (i % 3);
      const y = 8 + Math.floor(i / 3);

      gladiator.position = { x, y };
      gladiator.hp = gladiator.maxHp;
      gladiator.mana = gladiator.maxMana;
      gladiator.ammo = GAME_CONSTANTS.AMMO_COUNT;
      gladiator.needsReload = false;
      mapTiles[x][y] = gId;
      bottomTeamGladiators.push(gId);
    }

    // Top team (opponent) - rows 0-1
    const topTeamGladiators: string[] = [];
    for (let i = 0; i < GAME_CONSTANTS.MAX_GLADIATORS_IN_BATTLE; i++) {
      const gId = opponentTeam.battleGladiatorIds[i];
      if (!gId) continue;

      const gladiator = this.gameState.gladiators.get(gId);
      if (!gladiator) continue;

      const x = 2 + (i % 3);
      const y = Math.floor(i / 3);

      gladiator.position = { x, y };
      gladiator.hp = gladiator.maxHp;
      gladiator.mana = gladiator.maxMana;
      gladiator.ammo = GAME_CONSTANTS.AMMO_COUNT;
      gladiator.needsReload = false;
      mapTiles[x][y] = gId;
      topTeamGladiators.push(gId);
    }

    // Interleave turn order
    for (let i = 0; i < Math.max(bottomTeamGladiators.length, topTeamGladiators.length); i++) {
      if (i < bottomTeamGladiators.length) turnOrder.push(bottomTeamGladiators[i]);
      if (i < topTeamGladiators.length) turnOrder.push(topTeamGladiators[i]);
    }

    this.battleState = {
      round: 1,
      turnOrder,
      currentTurnIndex: 0,
      mapTiles,
      clipMask,
      bottomTeamId: playerTeam.id,
      topTeamId: opponentTeam.id,
      battleEnded: false,
      winningTeamId: null,
      messages: ['Taistelu alkaa!'],
    };
  }

  private parseClipMap(clipMask: boolean[][]): void {
    // The clip map is stored in assets.maps[0].clip
    // Red pixels (RGB: 237, 28, 36) indicate blocked tiles
    const clipImage = this.context.assets.maps[0]?.clip;
    if (!clipImage) return;

    // Create a temporary canvas to read pixel data
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = clipImage.width;
    tempCanvas.height = clipImage.height;
    const ctx = tempCanvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(clipImage, 0, 0);
    const imageData = ctx.getImageData(0, 0, clipImage.width, clipImage.height);
    const pixels = imageData.data;

    // Each tile corresponds to a region in the clip map
    // The clip map dimensions should match: MAP_WIDTH * tileSize x MAP_HEIGHT * tileSize
    const tileWidth = Math.floor(clipImage.width / GAME_CONSTANTS.MAP_WIDTH);
    const tileHeight = Math.floor(clipImage.height / GAME_CONSTANTS.MAP_HEIGHT);

    for (let tileX = 0; tileX < GAME_CONSTANTS.MAP_WIDTH; tileX++) {
      for (let tileY = 0; tileY < GAME_CONSTANTS.MAP_HEIGHT; tileY++) {
        // Sample the center of each tile
        const pixelX = Math.floor(tileX * tileWidth + tileWidth / 2);
        const pixelY = Math.floor(tileY * tileHeight + tileHeight / 2);
        const idx = (pixelY * clipImage.width + pixelX) * 4;

        const r = pixels[idx];
        const g = pixels[idx + 1];
        const b = pixels[idx + 2];

        // Check for red pixels (237, 28, 36) with some tolerance
        const isRed = r > 200 && g < 80 && b < 80;
        clipMask[tileX][tileY] = isRed;
      }
    }
  }

  update(deltaTime: number): void {
    // Always update animations
    this.animationSystem.update(deltaTime);

    if (!this.battleState || this.battleState.battleEnded) return;

    // Check for battle end
    this.checkBattleEnd();

    // If it's AI turn, execute it (but wait for previous AI action to complete)
    // In spectator mode, don't wait for animations - proceed immediately for faster playback
    const shouldWaitForAnimations = !this.spectatorMode && this.animationSystem.hasPendingAnimations();
    if (!this.battleState.battleEnded && !shouldWaitForAnimations && !this.aiTurnInProgress) {
      const currentGladiator = this.getCurrentGladiator();
      const isAITurn = currentGladiator && (
        this.spectatorMode ||
        currentGladiator.ownerId !== this.battleState.bottomTeamId
      );
      if (isAITurn) {
        this.aiTurnInProgress = true;
        this.executeAITurn(currentGladiator!);
      }
    }
  }

  render(canvas: Canvas): void {
    if (!this.gameState || !this.battleState) {
      canvas.clear(CONSTANTS.BATTLE_BACKGROUND);
      canvas.drawText('Loading battle...', 400, 260, CONSTANTS.BIG_FONT, '#ffffff', 'center');
      return;
    }

    canvas.clear(CONSTANTS.BATTLE_BACKGROUND);

    // Draw map background
    this.renderMap(canvas);

    // Draw gladiators on map
    this.renderGladiators(canvas);

    // Draw animations (damage numbers, hit markers)
    this.animationSystem.render(canvas);

    // Draw UI
    this.renderUI(canvas);

    // Draw messages
    this.renderMessages(canvas);
  }

  private renderMap(canvas: Canvas): void {
    const mapImage = this.context.assets.maps[0]?.image;
    if (mapImage) {
      canvas.drawImage(mapImage, 0, 60, CONSTANTS.MAP_WIDTH * CONSTANTS.MAP_TILE_SIZE + 50, CONSTANTS.MAP_HEIGHT * CONSTANTS.MAP_TILE_SIZE + 20);
    } else {
      // Fallback grid
      const startX = 50;
      const startY = 60;
      const tileSize = CONSTANTS.MAP_TILE_SIZE;

      for (let x = 0; x < GAME_CONSTANTS.MAP_WIDTH; x++) {
        for (let y = 0; y < GAME_CONSTANTS.MAP_HEIGHT; y++) {
          const px = startX + x * tileSize;
          const py = startY + y * tileSize;

          const isBlocked = this.battleState!.clipMask[x]?.[y];
          const color = isBlocked ? '#442222' : ((x + y) % 2 === 0 ? '#446644' : '#335533');

          canvas.drawRect(px, py, tileSize - 1, tileSize - 1, color);
        }
      }
    }
  }

  private renderGladiators(canvas: Canvas): void {
    const startX = 50;
    const startY = 60;
    const tileSize = CONSTANTS.MAP_TILE_SIZE;

    // Build a map of positions to alive gladiators for occlusion checking
    const aliveAtPosition = new Map<string, string>();
    for (const gladiatorId of this.battleState!.turnOrder) {
      const gladiator = this.gameState!.gladiators.get(gladiatorId);
      if (gladiator?.position && isAlive(gladiator)) {
        const posKey = `${gladiator.position.x},${gladiator.position.y}`;
        aliveAtPosition.set(posKey, gladiatorId);
      }
    }

    // Collect visible gladiators and sort by Y position so lower rows render on top
    const visibleGladiators: { id: string; gladiator: GladiatorData }[] = [];
    for (const gladiatorId of this.battleState!.turnOrder) {
      const gladiator = this.gameState!.gladiators.get(gladiatorId);
      if (!gladiator || !gladiator.position) continue;

      const posKey = `${gladiator.position.x},${gladiator.position.y}`;
      if (!isAlive(gladiator) && aliveAtPosition.has(posKey)) continue;

      visibleGladiators.push({ id: gladiatorId, gladiator });
    }
    visibleGladiators.sort((a, b) => a.gladiator.position!.y - b.gladiator.position!.y);

    // Pass 1: Draw portraits, highlights, team indicators, dead overlays, click regions
    for (const { id: gladiatorId, gladiator } of visibleGladiators) {
      const px = startX + gladiator.position!.x * tileSize;
      const py = startY + gladiator.position!.y * tileSize;

      // Team color indicator
      const isBottomTeam = gladiator.ownerId === this.battleState!.bottomTeamId;
      const teamColor = isBottomTeam ? '#0000ff' : '#ff0000';

      // Highlight valid target
      const isValidTarget = this.validTargets.includes(gladiatorId);
      if (isValidTarget) {
        canvas.drawRect(px - 3, py - 3, tileSize + 4, tileSize + 4, '#ff00ff');
      }

      // Highlight current turn
      const isCurrentTurn = gladiator.id === this.getCurrentGladiator()?.id;
      if (isCurrentTurn) {
        canvas.drawRect(px - 2, py - 2, tileSize + 2, tileSize + 2, '#ffff00');
      }

      // Portrait
      const portrait = this.context.assets.gladiators[gladiator.race.id];
      if (portrait) {
        canvas.drawImage(portrait, px, py, tileSize - 2, tileSize - 2);
      }

      // Team indicator
      canvas.drawRect(px, py + tileSize - 6, tileSize - 2, 4, teamColor);

      // Dead indicator
      if (!isAlive(gladiator)) {
        canvas.drawRect(px, py, tileSize - 2, tileSize - 2, 'rgba(0, 0, 0, 0.6)');
        canvas.drawText('X', px + tileSize / 2 - 5, py + tileSize / 2 - 10, CONSTANTS.BIG_FONT, '#ff0000');
      }

      // Add clickable region for targeting
      if (isValidTarget) {
        canvas.addClickableRegion({
          id: `target-${gladiatorId}`,
          x: px,
          y: py,
          width: tileSize,
          height: tileSize,
          onClick: () => this.handleTargetClick(gladiatorId),
        });
      }
    }

    // Pass 2: Draw HP bars on top of all portraits
    for (const { gladiator } of visibleGladiators) {
      if (!isAlive(gladiator)) continue;
      const px = startX + gladiator.position!.x * tileSize;
      const py = startY + gladiator.position!.y * tileSize;

      const hpRatio = gladiator.hp / gladiator.maxHp;
      const hpColor = hpRatio > 0.5 ? '#00ff00' : (hpRatio > 0.25 ? '#ffff00' : '#ff0000');
      canvas.drawRect(px, py - 5, (tileSize - 2) * hpRatio, 3, hpColor);
    }
  }

  private handleTargetClick(targetId: string): void {
    const attacker = this.getCurrentGladiator();
    const target = this.gameState?.gladiators.get(targetId);
    if (!attacker || !target) return;

    if (this.targetingMode === 'ranged') {
      this.rangedAttack(attacker, target);
    } else if (this.targetingMode === 'spell' && this.selectedSpell) {
      this.castSpell(attacker, target, this.selectedSpell);
    }
  }

  private renderUI(canvas: Canvas): void {
    // Right panel - current gladiator info
    const panelX = 310;
    const panelY = 60;
    const panelWidth = 480;
    const panelHeight = 150;

    canvas.drawRect(panelX, panelY, panelWidth, panelHeight, '#200020');
    canvas.drawRectOutline(panelX, panelY, panelWidth, panelHeight, '#aa00aa');

    // Round counter
    canvas.drawText(`Kierros: ${this.battleState!.round}/${GAME_CONSTANTS.MAX_BATTLE_ROUNDS}`, panelX + 10, panelY + 8, CONSTANTS.MAIN_FONT, '#ffffff');

    const currentGladiator = this.getCurrentGladiator();
    if (currentGladiator) {
      // In spectator mode, no one is "player"
      const isPlayerTurn = !this.spectatorMode &&
        currentGladiator.ownerId === this.battleState!.bottomTeamId;

      // Portrait
      const portrait = this.context.assets.gladiators[currentGladiator.race.id];
      if (portrait) {
        canvas.drawImage(portrait, panelX + 10, panelY + 30, 64, 64);
      }

      // Name and stats
      canvas.drawText(currentGladiator.name, panelX + 85, panelY + 35, CONSTANTS.GLADIATOR_NAME_FONT, '#ffffff');
      canvas.drawText(currentGladiator.race.name, panelX + 85, panelY + 55, CONSTANTS.MSG_FONT, '#aaaaaa');

      canvas.drawText(`HP: ${currentGladiator.hp}/${currentGladiator.maxHp}`, panelX + 85, panelY + 75, CONSTANTS.MAIN_FONT, '#88ff88');
      canvas.drawText(`Mana: ${currentGladiator.mana}/${currentGladiator.maxMana}`, panelX + 85, panelY + 92, CONSTANTS.MAIN_FONT, '#8888ff');

      // Equipment
      const meleeWpn = getMeleeWeapon(currentGladiator);
      const rangedWpn = getRangedWeapon(currentGladiator);

      canvas.drawText(`Lähitaistelu: ${meleeWpn?.name ?? 'Nyrkki'} (${getMeleeDmg(currentGladiator)})`, panelX + 200, panelY + 35, CONSTANTS.MSG_FONT, '#cccccc');
      if (rangedWpn) {
        const ammoText = currentGladiator.needsReload ? ' [LATAA]' : ` (${currentGladiator.ammo})`;
        canvas.drawText(`Etäase: ${rangedWpn.name}${ammoText}`, panelX + 200, panelY + 52, CONSTANTS.MSG_FONT, '#cccccc');
      }

      // Turn indicator
      const [turnText, turnColor] = this.getTurnIndicator(currentGladiator, isPlayerTurn);
      canvas.drawText(turnText, panelX + panelWidth - 10, panelY + 10, CONSTANTS.MAIN_FONT, turnColor, 'right');

      // Action buttons (only for player)
      if (isPlayerTurn && isAlive(currentGladiator)) {
        this.renderActionButtons(canvas, currentGladiator);
      }
    }

    // Battle end screen
    if (this.battleState!.battleEnded) {
      this.renderBattleEndScreen(canvas);
    }
  }

  private getTurnIndicator(gladiator: GladiatorData, isPlayerTurn: boolean): [string, string] {
    if (this.spectatorMode) {
      const teamName = gladiator.ownerId === this.battleState!.bottomTeamId
        ? 'Joukkue 1' : 'Joukkue 2';
      return [`[KATSELU] ${teamName}`, '#ffff00'];
    }
    if (isPlayerTurn) {
      return ['SINUN VUOROSI', '#00ff00'];
    }
    return ['Vastustajan vuoro...', '#ff6666'];
  }

  private renderActionButtons(canvas: Canvas, gladiator: GladiatorData): void {
    const btnY = 220;
    const btnHeight = 28;
    const btnWidth = 75;
    const btnX = 310;

    // Movement directions
    canvas.drawText('Liiku:', btnX, btnY, CONSTANTS.MAIN_FONT, '#ffffff');

    const directionGrid = [
      ['NW', 'N', 'NE'],
      ['W', null, 'E'],
      ['SW', 'S', 'SE'],
    ];

    const gridStartX = btnX + 50;
    const gridStartY = btnY - 10;
    const gridBtnSize = 28;

    for (let row = 0; row < 3; row++) {
      for (let col = 0; col < 3; col++) {
        const dir = directionGrid[row][col];
        if (!dir) continue;

        const dbx = gridStartX + col * gridBtnSize;
        const dby = gridStartY + row * gridBtnSize;

        // Check if move is valid
        const canMove = this.canMoveInDirection(gladiator, dir);
        const bgColor = canMove ? '#006600' : '#333333';

        canvas.drawButton(
          `move-${dir}`,
          dir,
          dbx,
          dby,
          gridBtnSize - 2,
          gridBtnSize - 2,
          CONSTANTS.MSG_FONT,
          '#ffffff',
          bgColor,
          () => canMove && this.moveGladiator(dir)
        );
      }
    }

    // Ranged attack button
    const rangedWpn = getRangedWeapon(gladiator);
    if (rangedWpn && gladiator.ammo > 0) {
      if (gladiator.needsReload) {
        canvas.drawButton(
          'reload',
          'Lataa',
          btnX + 165,
          btnY - 10,
          btnWidth,
          btnHeight,
          CONSTANTS.MAIN_FONT,
          '#ffffff',
          '#666600',
          () => this.reloadCrossbow(gladiator)
        );
      } else {
        const isActive = this.targetingMode === 'ranged';
        canvas.drawButton(
          'shoot',
          isActive ? 'Peruuta' : 'Ammu',
          btnX + 165,
          btnY - 10,
          btnWidth,
          btnHeight,
          CONSTANTS.MAIN_FONT,
          '#ffffff',
          isActive ? '#aa0000' : '#660066',
          () => this.toggleRangedMode(gladiator)
        );
      }
    }

    // Spell buttons
    const knownSpells = getKnownSpells(gladiator);
    if (knownSpells.length > 0) {
      canvas.drawText('Loitsut:', btnX + 165, btnY + 25, CONSTANTS.MSG_FONT, '#ffffff');

      for (let i = 0; i < knownSpells.length; i++) {
        const spell = knownSpells[i];
        const spellBtnX = btnX + 165 + (i % 2) * (btnWidth + 5);
        const spellBtnY = btnY + 40 + Math.floor(i / 2) * (btnHeight + 3);

        const canCast = gladiator.mana >= spell.manaCost;
        const isSelected = this.targetingMode === 'spell' && this.selectedSpell?.id === spell.id;

        canvas.drawButton(
          `spell-${i}`,
          spell.name.substring(0, 10),
          spellBtnX,
          spellBtnY,
          btnWidth,
          btnHeight - 3,
          CONSTANTS.MSG_FONT,
          '#ffffff',
          isSelected ? '#aa0000' : (canCast ? '#4a2a8a' : '#333333'),
          () => canCast && this.toggleSpellMode(gladiator, spell)
        );
      }
    }

    // Skip turn button
    canvas.drawButton(
      'skip',
      'Ohita',
      btnX + 300,
      btnY + 60,
      btnWidth,
      btnHeight,
      CONSTANTS.MAIN_FONT,
      '#ffffff',
      '#666666',
      () => this.endTurn()
    );

    // Autoplay round button
    canvas.drawButton(
      'autoplay',
      'Pelaa kierros',
      btnX + 380,
      btnY + 60,
      95,
      btnHeight,
      CONSTANTS.MAIN_FONT,
      '#ffffff',
      '#884400',
      () => this.startAutoplay()
    );

    // Targeting mode instructions
    if (this.targetingMode !== 'none') {
      const targetText = this.targetingMode === 'ranged'
        ? 'Klikkaa vihollista ammuttavaksi'
        : `Klikkaa kohdetta (${isFriendlySpell(this.selectedSpell!) ? 'ystävä' : 'vihollinen'})`;
      canvas.drawText(targetText, btnX + 50, btnY + 95, CONSTANTS.MSG_FONT, '#ffff00');
    }
  }

  private renderMessages(canvas: Canvas): void {
    const msgX = 310;
    const msgY = 420;
    const msgWidth = 480;
    const msgHeight = 100;

    canvas.drawRect(msgX, msgY, msgWidth, msgHeight, '#000000');
    canvas.drawRectOutline(msgX, msgY, msgWidth, msgHeight, '#666666');

    const allMessages = this.battleState!.messages;
    const totalMessages = allMessages.length;

    // Calculate the range of messages to display
    // scrollOffset of 0 means show newest messages
    // scrollOffset of 1 means scroll up by 1, showing 1 older message
    const endIndex = Math.max(0, totalMessages - this.messageScrollOffset);
    const startIndex = Math.max(0, endIndex - MESSAGE_LOG_VISIBLE_LINES);

    const visibleMessages = allMessages.slice(startIndex, endIndex);
    for (let i = 0; i < visibleMessages.length; i++) {
      const msg = visibleMessages[i];
      if (msg) {
        canvas.drawText(msg, msgX + 10, msgY + 10 + i * MESSAGE_LOG_LINE_HEIGHT, CONSTANTS.MSG_FONT, '#cccccc');
      }
    }

    // Show scroll indicators if there are more messages
    const canScrollUp = startIndex > 0;
    const canScrollDown = this.messageScrollOffset > 0;

    if (canScrollUp) {
      // Up arrow indicator
      canvas.drawButton(
        'msg-scroll-up',
        '^',
        msgX + msgWidth - 25,
        msgY + 5,
        20,
        20,
        'bold 14px "comic sans ms"',
        '#ffffff',
        '#444444',
        () => this.scrollMessagesUp()
      );
    }

    if (canScrollDown) {
      // Down arrow indicator
      canvas.drawButton(
        'msg-scroll-down',
        'v',
        msgX + msgWidth - 25,
        msgY + msgHeight - 25,
        20,
        20,
        'bold 14px "comic sans ms"',
        '#ffffff',
        '#444444',
        () => this.scrollMessagesDown()
      );
    }
  }

  private scrollMessagesUp(): void {
    if (!this.battleState) return;
    const maxScroll = Math.max(0, this.battleState.messages.length - MESSAGE_LOG_VISIBLE_LINES);
    this.messageScrollOffset = Math.min(this.messageScrollOffset + 1, maxScroll);
  }

  private scrollMessagesDown(): void {
    this.messageScrollOffset = Math.max(0, this.messageScrollOffset - 1);
  }

  private renderBattleEndScreen(canvas: Canvas): void {
    canvas.drawRect(150, 180, 500, 150, '#000000');
    canvas.drawRectOutline(150, 180, 500, 150, '#ffffff');

    const winner = this.battleState!.winningTeamId === this.battleState!.bottomTeamId;
    const resultText = winner ? 'VOITTO!' : 'TAPPIO';
    const resultColor = winner ? '#00ff00' : '#ff0000';

    canvas.drawText(resultText, 400, 210, 'bold 40px "comic sans ms"', resultColor, 'center');
    canvas.drawText('Paina ESC palataksesi', 400, 280, CONSTANTS.MAIN_FONT, '#ffffff', 'center');

    canvas.drawButton(
      'end-battle',
      'Jatka',
      350,
      300,
      100,
      30,
      CONSTANTS.MAIN_BUTTON_FONT,
      '#ffffff',
      '#006600',
      () => {
        // In spectator mode, go directly to main menu
        // Otherwise, show battle summary then kill leaderboard
        if (this.spectatorMode) {
          this.context.onScreenChange(ScreenState.MAIN_MENU);
        } else {
          this.context.onScreenChange(ScreenState.BATTLE_SUMMARY);
        }
      }
    );
  }

  private getCurrentGladiator(): GladiatorData | null {
    if (!this.battleState || !this.gameState) return null;

    // Find next alive, non-stunned gladiator
    while (this.battleState.currentTurnIndex < this.battleState.turnOrder.length) {
      const gId = this.battleState.turnOrder[this.battleState.currentTurnIndex];
      const gladiator = this.gameState.gladiators.get(gId);
      if (gladiator && isAlive(gladiator)) {
        // Check if stunned - skip turn but tick effects
        if (isStunned(gladiator)) {
          this.addMessage(`${gladiator.name} on lamaantunut ja ohittaa vuoronsa.`);
          tickEffects(gladiator);
          this.battleState.currentTurnIndex++;
          continue;
        }
        return gladiator;
      }
      this.battleState.currentTurnIndex++;
    }

    // Start new round
    this.battleState.currentTurnIndex = 0;
    this.battleState.round++;
    return this.getCurrentGladiator();
  }

  // BFS pathfinding: find direction for first step toward nearest enemy
  private findBFSDirection(gladiator: GladiatorData, enemies: GladiatorData[]): string | null {
    if (!gladiator.position || !this.battleState || !this.gameState) return null;

    const startX = gladiator.position.x;
    const startY = gladiator.position.y;
    const mapW = GAME_CONSTANTS.MAP_WIDTH;
    const mapH = GAME_CONSTANTS.MAP_HEIGHT;

    // Build set of enemy positions for adjacency check
    const enemyPositions = new Set<string>();
    for (const e of enemies) {
      if (e.position) enemyPositions.add(`${e.position.x},${e.position.y}`);
    }

    // Check if already adjacent to an enemy (no movement needed)
    for (const [, delta] of DIR_ENTRIES) {
      const nx = startX + delta.dx;
      const ny = startY + delta.dy;
      if (enemyPositions.has(`${nx},${ny}`)) return null;
    }

    // BFS: visited stores the first direction taken from start
    const visited = new Map<string, string>(); // posKey -> firstDir
    const queue: { x: number; y: number; firstDir: string }[] = [];

    // Seed with all valid first moves
    for (const [dirName, delta] of DIR_ENTRIES) {
      const nx = startX + delta.dx;
      const ny = startY + delta.dy;
      if (nx < 0 || nx >= mapW || ny < 0 || ny >= mapH) continue;
      if (this.battleState.clipMask[nx]?.[ny]) continue;

      const occupantId = this.battleState.mapTiles[nx]?.[ny];
      if (occupantId) {
        const occupant = this.gameState.gladiators.get(occupantId);
        if (occupant && isAlive(occupant)) continue; // Can't walk through alive gladiators
      }

      const key = `${nx},${ny}`;
      if (visited.has(key)) continue;
      visited.set(key, dirName);

      // Check if this tile is adjacent to an enemy
      for (const [, d2] of DIR_ENTRIES) {
        if (enemyPositions.has(`${nx + d2.dx},${ny + d2.dy}`)) {
          return dirName;
        }
      }

      queue.push({ x: nx, y: ny, firstDir: dirName });
    }

    // BFS loop
    let head = 0;
    while (head < queue.length) {
      const { x, y, firstDir } = queue[head++];

      for (const [, delta] of DIR_ENTRIES) {
        const nx = x + delta.dx;
        const ny = y + delta.dy;
        if (nx < 0 || nx >= mapW || ny < 0 || ny >= mapH) continue;
        if (this.battleState.clipMask[nx]?.[ny]) continue;

        const key = `${nx},${ny}`;
        if (visited.has(key)) continue;

        const occupantId = this.battleState.mapTiles[nx]?.[ny];
        if (occupantId) {
          const occupant = this.gameState.gladiators.get(occupantId);
          if (occupant && isAlive(occupant)) continue;
        }

        visited.set(key, firstDir);

        // Check if this tile is adjacent to an enemy
        for (const [, d2] of DIR_ENTRIES) {
          if (enemyPositions.has(`${nx + d2.dx},${ny + d2.dy}`)) {
            return firstDir;
          }
        }

        queue.push({ x: nx, y: ny, firstDir });
      }
    }

    return null; // No path found
  }

  private canMoveInDirection(gladiator: GladiatorData, dir: string): boolean {
    if (!gladiator.position) return false;

    // Check if rooted (cannot move)
    if (isRooted(gladiator)) return false;

    const delta = DIRECTIONS[dir];
    if (!delta) return false;

    const newX = gladiator.position.x + delta.dx;
    const newY = gladiator.position.y + delta.dy;

    // Bounds check
    if (newX < 0 || newX >= GAME_CONSTANTS.MAP_WIDTH) return false;
    if (newY < 0 || newY >= GAME_CONSTANTS.MAP_HEIGHT) return false;

    // Clip check
    if (this.battleState!.clipMask[newX]?.[newY]) return false;

    // Check for friendly gladiator (can't move to their tile)
    const occupantId = this.battleState!.mapTiles[newX]?.[newY];
    if (occupantId) {
      const occupant = this.gameState?.gladiators.get(occupantId);
      if (occupant && isAlive(occupant) && occupant.ownerId === gladiator.ownerId) {
        return false;
      }
    }

    return true;
  }

  private moveGladiator(dir: string): void {
    const gladiator = this.getCurrentGladiator();
    if (!gladiator || !gladiator.position) return;

    const delta = DIRECTIONS[dir];
    if (!delta) return;

    const newX = gladiator.position.x + delta.dx;
    const newY = gladiator.position.y + delta.dy;

    // Validate move is within bounds
    if (newX < 0 || newX >= GAME_CONSTANTS.MAP_WIDTH ||
        newY < 0 || newY >= GAME_CONSTANTS.MAP_HEIGHT) {
      console.warn(`Invalid move: ${dir} would go out of bounds (${newX}, ${newY})`);
      return;
    }

    // Check clip mask
    if (this.battleState!.clipMask[newX]?.[newY]) {
      console.warn(`Invalid move: ${dir} blocked by clip mask`);
      return;
    }

    // Check if enemy is at target
    const targetId = this.battleState!.mapTiles[newX]?.[newY];
    if (targetId) {
      const target = this.gameState!.gladiators.get(targetId);
      if (target && isAlive(target) && target.ownerId !== gladiator.ownerId) {
        // Melee attack!
        this.meleeAttack(gladiator, target);
        this.endTurn();
        return;
      }
      // Can't move to occupied tile (friendly or dead)
      if (target && isAlive(target)) {
        console.warn(`Invalid move: ${dir} blocked by friendly gladiator`);
        return;
      }
    }

    // Move
    this.battleState!.mapTiles[gladiator.position.x][gladiator.position.y] = null;
    this.battleState!.mapTiles[newX][newY] = gladiator.id;
    gladiator.position = { x: newX, y: newY };

    this.addMessage(`${gladiator.name} liikkuu ${dir}.`);
    this.endTurn();
  }

  private meleeAttack(attacker: GladiatorData, defender: GladiatorData): void {
    const weapon = getMeleeWeapon(attacker);
    const defWeapon = getMeleeWeapon(defender);

    const atkSkill = getAtkSkillForWeapon(attacker, weapon) + (weapon?.hitBonus ?? 0);
    const defSkill = getDefSkillForWeapon(defender, defWeapon) + (defWeapon?.blockBonus ?? 0);

    // Apply hit chance modifiers from BLESSING/CURSE
    const attackerMod = getHitChanceModifier(attacker);
    const defenderMod = getHitChanceModifier(defender);

    const hitChance = 50 + atkSkill - defSkill + attackerMod - defenderMod;
    const roll = random(100);
    const hit = roll < hitChance;

    if (hit) {
      let damage = rollDiceStr(getMeleeDmg(attacker));

      // Critical hit
      if (random(100) < GAME_CONSTANTS.CRITICAL_HIT_CHANCE_PERCENTAGE) {
        damage = Math.floor(damage * GAME_CONSTANTS.CRITICAL_HIT_DMG_MULTIPLIER);
        this.addMessage('Kriittinen osuma!');
      }

      // Race of month bonus
      if (attacker.race.id === this.gameState!.raceOfMonth.id) {
        damage *= 2;
      }

      // Armor reduction (including PROTECT spell bonus)
      const armor = getArmorValue(defender) + getProtectionBonus(defender);
      damage = Math.max(0, damage - armor);

      defender.hp -= damage;

      // Spawn damage number animation
      if (defender.position) {
        this.animationSystem.addDamageNumber(defender.id, damage, defender.position.x, defender.position.y);
        this.animationSystem.addHitMarker(HitMarkerType.HIT, defender.position.x, defender.position.y);
      }

      const weaponName = weapon?.name ?? 'nyrkillä';
      this.addMessage(`${attacker.name} osuu ${defender.name}:iin (${weaponName}) tehden ${damage} vahinkoa.`);

      if (!isAlive(defender)) {
        this.addMessage(`${defender.name} kaatuu!`);
        if (defender.position) {
          this.animationSystem.addHitMarker(HitMarkerType.KO, defender.position.x, defender.position.y);
        }
        attacker.takeOuts1++;
        attacker.takeOuts2++;
      }
    } else {
      // Spawn miss indicator
      if (defender.position) {
        this.animationSystem.addDamageNumber(defender.id, 0, defender.position.x, defender.position.y, true);
        this.animationSystem.addHitMarker(HitMarkerType.MISS, defender.position.x, defender.position.y);
      }
      const missWeaponName = weapon?.name ?? 'nyrkillä';
      this.addMessage(`${attacker.name} hyökkää (${missWeaponName}) mutta ${defender.name} väistää!`);
    }
  }

  private reloadCrossbow(gladiator: GladiatorData): void {
    if (gladiator.needsReload) {
      gladiator.needsReload = false;
      this.addMessage(`${gladiator.name} lataa varsijousensa.`);
      this.endTurn();
    }
  }

  private toggleRangedMode(gladiator: GladiatorData): void {
    if (this.targetingMode === 'ranged') {
      this.targetingMode = 'none';
      this.validTargets = [];
    } else {
      this.targetingMode = 'ranged';
      this.selectedSpell = null;
      this.validTargets = this.getValidRangedTargets(gladiator);
    }
  }

  private toggleSpellMode(gladiator: GladiatorData, spell: Spell): void {
    if (this.targetingMode === 'spell' && this.selectedSpell?.id === spell.id) {
      this.targetingMode = 'none';
      this.selectedSpell = null;
      this.validTargets = [];
    } else {
      this.targetingMode = 'spell';
      this.selectedSpell = spell;
      this.validTargets = this.getValidSpellTargets(gladiator, spell);
    }
  }

  private getValidRangedTargets(attacker: GladiatorData): string[] {
    const targets: string[] = [];
    for (const gId of this.battleState!.turnOrder) {
      const g = this.gameState!.gladiators.get(gId);
      if (g && isAlive(g) && g.ownerId !== attacker.ownerId) {
        targets.push(gId);
      }
    }
    return targets;
  }

  private getValidSpellTargets(caster: GladiatorData, spell: Spell): string[] {
    const targets: string[] = [];
    const friendly = isFriendlySpell(spell);

    for (const gId of this.battleState!.turnOrder) {
      const g = this.gameState!.gladiators.get(gId);
      if (!g || !isAlive(g)) continue;

      if (friendly) {
        // Friendly spells target allies (including self)
        if (g.ownerId === caster.ownerId) {
          targets.push(gId);
        }
      } else {
        // Hostile spells target enemies
        if (g.ownerId !== caster.ownerId) {
          targets.push(gId);
        }
      }
    }
    return targets;
  }

  private rangedAttack(attacker: GladiatorData, target: GladiatorData): void {
    const weapon = getRangedWeapon(attacker);
    if (!weapon || attacker.ammo <= 0) return;

    const atkSkill = getAtkSkillForWeapon(attacker, weapon);
    const defSkill = target.skills.abilities[0]; // Evasion

    // Apply hit chance modifiers from BLESSING/CURSE
    const attackerMod = getHitChanceModifier(attacker);
    const defenderMod = getHitChanceModifier(target);

    const hitChance = 50 + atkSkill - defSkill + attackerMod - defenderMod;
    const roll = random(100);
    const hit = roll < hitChance;

    // Use ammo
    attacker.ammo--;

    // Crossbow needs reload after each shot
    if (weapon.subtype === 1) { // CROSSBOW
      attacker.needsReload = true;
    }

    if (hit) {
      let damage = rollDiceStr(getRangedDmg(attacker));

      // Critical hit
      if (random(100) < GAME_CONSTANTS.CRITICAL_HIT_CHANCE_PERCENTAGE) {
        damage = Math.floor(damage * GAME_CONSTANTS.CRITICAL_HIT_DMG_MULTIPLIER);
        this.addMessage('Kriittinen osuma!');
      }

      // Race of month bonus
      if (attacker.race.id === this.gameState!.raceOfMonth.id) {
        damage *= 2;
      }

      // Armor reduction (including PROTECT spell bonus)
      const armor = getArmorValue(target) + getProtectionBonus(target);
      damage = Math.max(0, damage - armor);

      target.hp -= damage;

      // Spawn damage number animation
      if (target.position) {
        this.animationSystem.addDamageNumber(target.id, damage, target.position.x, target.position.y);
        this.animationSystem.addHitMarker(HitMarkerType.HIT, target.position.x, target.position.y);
      }

      this.addMessage(`${attacker.name} ampuu ${target.name}:ia (${weapon.name}) tehden ${damage} vahinkoa.`);

      if (!isAlive(target)) {
        this.addMessage(`${target.name} kaatuu!`);
        if (target.position) {
          this.animationSystem.addHitMarker(HitMarkerType.KO, target.position.x, target.position.y);
        }
        attacker.takeOuts1++;
        attacker.takeOuts2++;
      }
    } else {
      // Spawn miss indicator
      if (target.position) {
        this.animationSystem.addDamageNumber(target.id, 0, target.position.x, target.position.y, true);
        this.animationSystem.addHitMarker(HitMarkerType.MISS, target.position.x, target.position.y);
      }
      this.addMessage(`${attacker.name} ampuu (${weapon.name}) mutta ohittaa ${target.name}:n!`);
    }

    this.targetingMode = 'none';
    this.validTargets = [];
    this.endTurn();
  }

  private castSpell(caster: GladiatorData, target: GladiatorData, spell: Spell): void {
    if (caster.mana < spell.manaCost) return;

    // Consume mana
    caster.mana -= spell.manaCost;

    const friendly = isFriendlySpell(spell);

    if (!friendly) {
      // Hostile spell - check hit
      const atkSkill = getAtkSkillForSpell(caster, spell);
      const defSkill = target.skills.abilities[1]; // Magic resist

      const hitChance = 50 + atkSkill - defSkill;
      const roll = random(100);

      if (roll >= hitChance) {
        // Spell resisted
        if (target.position) {
          this.animationSystem.addDamageNumber(target.id, 0, target.position.x, target.position.y, true);
          this.animationSystem.addHitMarker(HitMarkerType.MISS, target.position.x, target.position.y);
        }
        this.addMessage(`${caster.name} käyttää ${spell.name} mutta ${target.name} vastustaa!`);
        this.targetingMode = 'none';
        this.selectedSpell = null;
        this.validTargets = [];
        this.endTurn();
        return;
      }
    }

    // Apply spell effect
    this.applySpellEffect(caster, target, spell);

    this.targetingMode = 'none';
    this.selectedSpell = null;
    this.validTargets = [];
    this.endTurn();
  }

  private applySpellEffect(caster: GladiatorData, target: GladiatorData, spell: Spell): void {
    const strength = spell.strength ? rollDiceStr(spell.strength) : 0;

    // Show spell hit marker
    if (target.position) {
      const markerType = isFriendlySpell(spell) ? HitMarkerType.SPELL_2 : HitMarkerType.SPELL_1;
      this.animationSystem.addHitMarker(markerType, target.position.x, target.position.y);
    }

    switch (spell.type) {
      case SPELL_TYPE.ATK:
        // Direct damage (spells bypass armor)
        target.hp -= strength;
        if (target.position) {
          this.animationSystem.addDamageNumber(target.id, strength, target.position.x, target.position.y);
        }
        this.addMessage(`${caster.name} käyttää ${spell.name} ${target.name}:iin tehden ${strength} vahinkoa.`);
        if (!isAlive(target)) {
          this.addMessage(`${target.name} kaatuu!`);
          if (target.position) {
            this.animationSystem.addHitMarker(HitMarkerType.KO, target.position.x, target.position.y);
          }
          caster.takeOuts1++;
          caster.takeOuts2++;
        }
        break;

      case SPELL_TYPE.HEAL:
        // Heal HP
        const healed = Math.min(strength, target.maxHp - target.hp);
        target.hp += healed;
        this.addMessage(`${caster.name} parantaa ${target.name}:ia ${healed} pistettä.`);
        break;

      case SPELL_TYPE.PROTECT:
        // Protection buff - adds armor bonus for duration
        addActiveEffect(target, SPELL_TYPE.PROTECT, strength, spell.duration ?? 10);
        this.addMessage(`${caster.name} suojaa ${target.name}:ia loitsulla ${spell.name} (+${strength} panssaria).`);
        break;

      case SPELL_TYPE.STR:
        // Strength buff - adds strength bonus for duration
        target.strBonus += strength;
        addActiveEffect(target, SPELL_TYPE.STR, strength, spell.duration ?? 10);
        this.addMessage(`${caster.name} vahvistaa ${target.name}:ia (+${strength} voimaa).`);
        break;

      case SPELL_TYPE.DRAIN_HEALTH:
        // Steal HP (spells bypass armor)
        target.hp -= strength;
        caster.hp = Math.min(caster.maxHp, caster.hp + strength);
        if (target.position) {
          this.animationSystem.addDamageNumber(target.id, strength, target.position.x, target.position.y);
        }
        this.addMessage(`${caster.name} imee ${strength} elämää ${target.name}:lta.`);
        if (!isAlive(target)) {
          this.addMessage(`${target.name} kaatuu!`);
          if (target.position) {
            this.animationSystem.addHitMarker(HitMarkerType.KO, target.position.x, target.position.y);
          }
          caster.takeOuts1++;
          caster.takeOuts2++;
        }
        break;

      case SPELL_TYPE.DRAIN_MANA:
        // Steal mana
        const drained = Math.min(strength, target.mana);
        target.mana -= drained;
        caster.mana = Math.min(caster.maxMana, caster.mana + drained);
        this.addMessage(`${caster.name} imee ${drained} manaa ${target.name}:lta.`);
        break;

      case SPELL_TYPE.MYSTIFICATION:
        // Stun effect - skip turns
        target.stunTurns = spell.duration ?? 1;
        this.addMessage(`${caster.name} lamaannuttaa ${target.name}:n loitsulla ${spell.name} (${target.stunTurns} vuoroa).`);
        break;

      case SPELL_TYPE.STOP:
        // Root effect - cannot move
        target.rootedTurns = spell.duration ?? 2;
        this.addMessage(`${caster.name} pysäyttää ${target.name}:n loitsulla ${spell.name} (${target.rootedTurns} vuoroa).`);
        break;

      case SPELL_TYPE.BLESSING:
        // Hit chance buff
        addActiveEffect(target, SPELL_TYPE.BLESSING, strength, spell.duration ?? 10);
        this.addMessage(`${caster.name} siunaa ${target.name}:ia (+${strength}% osuma).`);
        break;

      case SPELL_TYPE.CURSE:
        // Hit chance debuff
        addActiveEffect(target, SPELL_TYPE.CURSE, strength, spell.duration ?? 10);
        this.addMessage(`${caster.name} kiroaa ${target.name}:n (-${strength}% osuma).`);
        break;

      case SPELL_TYPE.REMOVE_SPELL:
        // Clear all active effects
        clearActiveEffects(target);
        this.addMessage(`${caster.name} poistaa loitsut ${target.name}:lta.`);
        break;

      default:
        this.addMessage(`${caster.name} käyttää ${spell.name}.`);
    }
  }

  private executeAITurn(gladiator: GladiatorData): void {
    if (!gladiator.position) {
      this.endTurn();
      return;
    }

    // 1. Check if crossbow needs reload
    const rangedWpn = getRangedWeapon(gladiator);
    if (rangedWpn && gladiator.needsReload && gladiator.ammo > 0) {
      setTimeout(() => this.reloadCrossbow(gladiator), this.getAIDelay());
      return;
    }

    // Gather allies and enemies
    const allies: GladiatorData[] = [];
    const enemies: GladiatorData[] = [];

    for (const gId of this.battleState!.turnOrder) {
      const g = this.gameState!.gladiators.get(gId);
      if (!g || !isAlive(g) || !g.position) continue;

      if (g.ownerId === gladiator.ownerId) {
        allies.push(g);
      } else {
        enemies.push(g);
      }
    }

    // 2. Check for heal spell + low HP ally
    const knownSpells = getKnownSpells(gladiator);
    const healSpell = knownSpells.find(s => s.type === SPELL_TYPE.HEAL && gladiator.mana >= s.manaCost);
    if (healSpell) {
      // Find ally with lowest HP percentage
      const lowHpAlly = allies
        .filter(a => a.hp < a.maxHp * 0.5) // Below 50% HP
        .sort((a, b) => (a.hp / a.maxHp) - (b.hp / b.maxHp))[0];

      if (lowHpAlly) {
        setTimeout(() => this.castSpell(gladiator, lowHpAlly, healSpell), this.getAIDelay());
        return;
      }
    }

    // Find nearest enemy for ranged/offensive decisions
    let nearestEnemy: GladiatorData | null = null;
    let nearestDist = 999;
    const gladPos = gladiator.position;

    for (const enemy of enemies) {
      if (!enemy.position) continue;
      const dist = Math.abs(enemy.position.x - gladPos.x) + Math.abs(enemy.position.y - gladPos.y);
      if (dist < nearestDist) {
        nearestDist = dist;
        nearestEnemy = enemy;
      }
    }

    if (!nearestEnemy) {
      this.endTurn();
      return;
    }

    // 3. Check for ranged weapon + ammo → ranged attack (enemies visible from any range)
    if (rangedWpn && gladiator.ammo > 0 && !gladiator.needsReload) {
      // AI uses ranged if enemy is more than 2 tiles away
      if (nearestDist > 2) {
        setTimeout(() => this.rangedAttack(gladiator, nearestEnemy!), this.getAIDelay());
        return;
      }
    }

    // 4. Check for offensive spell + mana
    const offensiveSpell = knownSpells.find(s =>
      (s.type === SPELL_TYPE.ATK || s.type === SPELL_TYPE.DRAIN_HEALTH) &&
      gladiator.mana >= s.manaCost
    );
    if (offensiveSpell && nearestDist > 1) {
      // Use offensive spell when not in melee range
      setTimeout(() => this.castSpell(gladiator, nearestEnemy, offensiveSpell), this.getAIDelay());
      return;
    }

    // Check for crowd control spells
    const ccSpell = knownSpells.find(s =>
      (s.type === SPELL_TYPE.MYSTIFICATION || s.type === SPELL_TYPE.STOP || s.type === SPELL_TYPE.CURSE) &&
      gladiator.mana >= s.manaCost
    );
    if (ccSpell && nearestDist <= 3 && random(100) < 30) {
      // 30% chance to use CC spell when enemy is close
      setTimeout(() => this.castSpell(gladiator, nearestEnemy, ccSpell), this.getAIDelay());
      return;
    }

    // Check for buff spells on self
    const buffSpell = knownSpells.find(s =>
      (s.type === SPELL_TYPE.STR || s.type === SPELL_TYPE.PROTECT || s.type === SPELL_TYPE.BLESSING) &&
      gladiator.mana >= s.manaCost
    );
    if (buffSpell && nearestDist > 3 && random(100) < 40) {
      // 40% chance to buff self when far from enemy
      setTimeout(() => this.castSpell(gladiator, gladiator, buffSpell), this.getAIDelay());
      return;
    }

    // 5. Fall back to melee movement using BFS pathfinding
    const bfsDir = this.findBFSDirection(gladiator, enemies);
    if (bfsDir && this.canMoveInDirection(gladiator, bfsDir)) {
      setTimeout(() => this.moveGladiator(bfsDir), this.getAIDelay());
    } else {
      this.endTurn();
    }
  }

  private startAutoplay(): void {
    if (!this.battleState || this.battleState.battleEnded) return;

    const startRound = this.battleState.round;
    this.addMessage(`--- Simuloidaan kierros ${startRound} ---`);

    // Clear any targeting mode
    this.targetingMode = 'none';
    this.selectedSpell = null;
    this.validTargets = [];

    // Simulate all turns until the round changes or battle ends
    let maxIterations = 100; // Safety limit
    while (this.battleState.round === startRound &&
           !this.battleState.battleEnded &&
           maxIterations > 0) {
      const gladiator = this.getCurrentGladiatorForSimulation();
      if (!gladiator) break;

      this.executeSimulatedTurn(gladiator);
      maxIterations--;
    }

    // Check battle end after simulation
    this.checkBattleEnd();

    if (!this.battleState.battleEnded) {
      this.addMessage(`--- Kierros ${startRound} simuloitu ---`);
    }
  }

  // Get current gladiator without the side effects of getCurrentGladiator
  private getCurrentGladiatorForSimulation(): GladiatorData | null {
    if (!this.battleState || !this.gameState) return null;

    while (this.battleState.currentTurnIndex < this.battleState.turnOrder.length) {
      const gId = this.battleState.turnOrder[this.battleState.currentTurnIndex];
      const gladiator = this.gameState.gladiators.get(gId);
      if (gladiator && isAlive(gladiator)) {
        if (isStunned(gladiator)) {
          this.addMessage(`${gladiator.name} on lamaantunut ja ohittaa vuoronsa.`);
          tickEffects(gladiator);
          this.battleState.currentTurnIndex++;
          continue;
        }
        return gladiator;
      }
      this.battleState.currentTurnIndex++;
    }

    // Start new round
    this.battleState.currentTurnIndex = 0;
    this.battleState.round++;

    // Return null to signal round change
    return null;
  }

  // Execute a turn without animations or delays (mirrors executeAITurn logic)
  private executeSimulatedTurn(gladiator: GladiatorData): void {
    if (!gladiator.position || !this.battleState || !this.gameState) {
      this.endTurnSimulated();
      return;
    }

    // 1. Check if crossbow needs reload
    const rangedWpn = getRangedWeapon(gladiator);
    if (rangedWpn && gladiator.needsReload && gladiator.ammo > 0) {
      gladiator.needsReload = false;
      this.addMessage(`${gladiator.name} lataa varsijousensa.`);
      this.endTurnSimulated();
      return;
    }

    // Gather allies and enemies
    const allies: GladiatorData[] = [];
    const enemies: GladiatorData[] = [];
    for (const gId of this.battleState.turnOrder) {
      const g = this.gameState.gladiators.get(gId);
      if (!g || !isAlive(g) || !g.position) continue;
      if (g.ownerId === gladiator.ownerId) {
        allies.push(g);
      } else {
        enemies.push(g);
      }
    }

    // 2. Check for heal spell + low HP ally
    const knownSpells = getKnownSpells(gladiator);
    const healSpell = knownSpells.find(s => s.type === SPELL_TYPE.HEAL && gladiator.mana >= s.manaCost);
    if (healSpell) {
      const lowHpAlly = allies
        .filter(a => a.hp < a.maxHp * 0.5)
        .sort((a, b) => (a.hp / a.maxHp) - (b.hp / b.maxHp))[0];
      if (lowHpAlly) {
        this.castSpellSimulated(gladiator, lowHpAlly, healSpell);
        return;
      }
    }

    if (enemies.length === 0) {
      this.endTurnSimulated();
      return;
    }

    // Find nearest enemy
    let nearestEnemy: GladiatorData | null = null;
    let nearestDist = 999;
    const gladPos = gladiator.position;

    for (const enemy of enemies) {
      if (!enemy.position) continue;
      const dist = Math.abs(enemy.position.x - gladPos.x) + Math.abs(enemy.position.y - gladPos.y);
      if (dist < nearestDist) {
        nearestDist = dist;
        nearestEnemy = enemy;
      }
    }

    if (!nearestEnemy || !nearestEnemy.position) {
      this.endTurnSimulated();
      return;
    }

    // 3. Ranged attack if enemy is far
    if (rangedWpn && gladiator.ammo > 0 && !gladiator.needsReload && nearestDist > 2) {
      this.rangedAttackSimulated(gladiator, nearestEnemy);
      return;
    }

    // 4. Offensive spell if not in melee range
    const offensiveSpell = knownSpells.find(s =>
      (s.type === SPELL_TYPE.ATK || s.type === SPELL_TYPE.DRAIN_HEALTH) &&
      gladiator.mana >= s.manaCost
    );
    if (offensiveSpell && nearestDist > 1) {
      this.castSpellSimulated(gladiator, nearestEnemy, offensiveSpell);
      return;
    }

    // CC spells
    const ccSpell = knownSpells.find(s =>
      (s.type === SPELL_TYPE.MYSTIFICATION || s.type === SPELL_TYPE.STOP || s.type === SPELL_TYPE.CURSE) &&
      gladiator.mana >= s.manaCost
    );
    if (ccSpell && nearestDist <= 3 && random(100) < 30) {
      this.castSpellSimulated(gladiator, nearestEnemy, ccSpell);
      return;
    }

    // Buff spells on self
    const buffSpell = knownSpells.find(s =>
      (s.type === SPELL_TYPE.STR || s.type === SPELL_TYPE.PROTECT || s.type === SPELL_TYPE.BLESSING) &&
      gladiator.mana >= s.manaCost
    );
    if (buffSpell && nearestDist > 3 && random(100) < 40) {
      this.castSpellSimulated(gladiator, gladiator, buffSpell);
      return;
    }

    // 5. Melee movement
    if (!nearestEnemy.position) {
      this.endTurnSimulated();
      return;
    }

    const bfsDir = this.findBFSDirection(gladiator, enemies);
    if (bfsDir && this.canMoveInDirection(gladiator, bfsDir)) {
      this.moveGladiatorSimulated(gladiator, bfsDir);
    } else if (!bfsDir && nearestDist === 1) {
      // Already adjacent to enemy — find direction and attack via move
      const dx = nearestEnemy.position!.x - gladiator.position!.x;
      const dy = nearestEnemy.position!.y - gladiator.position!.y;
      const dir = DIR_ENTRIES.find(([, d]) => d.dx === dx && d.dy === dy);
      if (dir) {
        this.moveGladiatorSimulated(gladiator, dir[0]);
      } else {
        this.endTurnSimulated();
      }
    } else {
      this.endTurnSimulated();
    }
  }

  // Move gladiator without animations
  private moveGladiatorSimulated(gladiator: GladiatorData, dir: string): void {
    if (!gladiator.position || !this.battleState || !this.gameState) return;

    const delta = DIRECTIONS[dir];
    if (!delta) {
      this.endTurnSimulated();
      return;
    }

    const newX = gladiator.position.x + delta.dx;
    const newY = gladiator.position.y + delta.dy;

    // Validate bounds
    if (newX < 0 || newX >= GAME_CONSTANTS.MAP_WIDTH ||
        newY < 0 || newY >= GAME_CONSTANTS.MAP_HEIGHT) {
      this.endTurnSimulated();
      return;
    }

    // Check for enemy at target - melee attack
    const targetId = this.battleState.mapTiles[newX]?.[newY];
    if (targetId) {
      const target = this.gameState.gladiators.get(targetId);
      if (target && isAlive(target) && target.ownerId !== gladiator.ownerId) {
        this.meleeAttackSimulated(gladiator, target);
        this.endTurnSimulated();
        return;
      }
      if (target && isAlive(target)) {
        this.endTurnSimulated();
        return;
      }
    }

    // Move
    this.battleState.mapTiles[gladiator.position.x][gladiator.position.y] = null;
    this.battleState.mapTiles[newX][newY] = gladiator.id;
    gladiator.position = { x: newX, y: newY };
    this.addMessage(`${gladiator.name} liikkuu.`);
    this.endTurnSimulated();
  }

  // Melee attack without animations
  private meleeAttackSimulated(attacker: GladiatorData, defender: GladiatorData): void {
    const weapon = getMeleeWeapon(attacker);
    const defWeapon = getMeleeWeapon(defender);

    const atkSkill = getAtkSkillForWeapon(attacker, weapon) + (weapon?.hitBonus ?? 0);
    const defSkill = getDefSkillForWeapon(defender, defWeapon) + (defWeapon?.blockBonus ?? 0);

    const attackerMod = getHitChanceModifier(attacker);
    const defenderMod = getHitChanceModifier(defender);

    const hitChance = 50 + atkSkill - defSkill + attackerMod - defenderMod;
    const roll = random(100);

    if (roll < hitChance) {
      let damage = rollDiceStr(getMeleeDmg(attacker));
      let isCrit = false;

      if (random(100) < GAME_CONSTANTS.CRITICAL_HIT_CHANCE_PERCENTAGE) {
        damage = Math.floor(damage * GAME_CONSTANTS.CRITICAL_HIT_DMG_MULTIPLIER);
        isCrit = true;
      }

      if (attacker.race.id === this.gameState!.raceOfMonth.id) {
        damage *= 2;
      }

      const armor = getArmorValue(defender) + getProtectionBonus(defender);
      damage = Math.max(0, damage - armor);

      defender.hp -= damage;

      const weaponName = weapon?.name ?? 'nyrkillä';
      if (isCrit) {
        this.addMessage(`${attacker.name} osuu kriittisesti ${defender.name}:iin (${weaponName})! (${damage} vahinkoa)`);
      } else {
        this.addMessage(`${attacker.name} osuu ${defender.name}:iin (${weaponName}) tehden ${damage} vahinkoa.`);
      }

      if (!isAlive(defender)) {
        this.addMessage(`${defender.name} kaatuu!`);
        attacker.takeOuts1++;
        attacker.takeOuts2++;
      }
    } else {
      const missWeaponName = weapon?.name ?? 'nyrkillä';
      this.addMessage(`${attacker.name} hyökkää (${missWeaponName}) mutta ${defender.name} väistää!`);
    }
  }

  // Ranged attack without animations
  private rangedAttackSimulated(attacker: GladiatorData, target: GladiatorData): void {
    const weapon = getRangedWeapon(attacker);
    if (!weapon || attacker.ammo <= 0) {
      this.endTurnSimulated();
      return;
    }

    const atkSkill = getAtkSkillForWeapon(attacker, weapon);
    const defSkill = target.skills.abilities[0];

    const attackerMod = getHitChanceModifier(attacker);
    const defenderMod = getHitChanceModifier(target);

    const hitChance = 50 + atkSkill - defSkill + attackerMod - defenderMod;
    const roll = random(100);

    attacker.ammo--;
    if (weapon.subtype === 1) {
      attacker.needsReload = true;
    }

    if (roll < hitChance) {
      let damage = rollDiceStr(getRangedDmg(attacker));

      if (random(100) < GAME_CONSTANTS.CRITICAL_HIT_CHANCE_PERCENTAGE) {
        damage = Math.floor(damage * GAME_CONSTANTS.CRITICAL_HIT_DMG_MULTIPLIER);
        this.addMessage('Kriittinen osuma!');
      }

      if (attacker.race.id === this.gameState!.raceOfMonth.id) {
        damage *= 2;
      }

      const armor = getArmorValue(target) + getProtectionBonus(target);
      damage = Math.max(0, damage - armor);
      target.hp -= damage;

      this.addMessage(`${attacker.name} ampuu ${target.name}:ia (${weapon.name}) tehden ${damage} vahinkoa.`);

      if (!isAlive(target)) {
        this.addMessage(`${target.name} kaatuu!`);
        attacker.takeOuts1++;
        attacker.takeOuts2++;
      }
    } else {
      this.addMessage(`${attacker.name} ampuu (${weapon.name}) mutta ohittaa ${target.name}:n!`);
    }

    this.endTurnSimulated();
  }

  // Cast spell without animations
  private castSpellSimulated(caster: GladiatorData, target: GladiatorData, spell: Spell): void {
    if (caster.mana < spell.manaCost) {
      this.endTurnSimulated();
      return;
    }

    caster.mana -= spell.manaCost;

    const friendly = isFriendlySpell(spell);

    if (!friendly) {
      const atkSkill = getAtkSkillForSpell(caster, spell);
      const defSkill = target.skills.abilities[1];

      const hitChance = 50 + atkSkill - defSkill;
      const roll = random(100);

      if (roll >= hitChance) {
        this.addMessage(`${caster.name} käyttää ${spell.name} mutta ${target.name} vastustaa!`);
        this.endTurnSimulated();
        return;
      }
    }

    // Apply spell effect (reuses applySpellEffect which handles messages and game state)
    this.applySpellEffect(caster, target, spell);
    this.endTurnSimulated();
  }

  // End turn without animations
  private endTurnSimulated(): void {
    if (!this.battleState || !this.gameState) return;

    const currentGId = this.battleState.turnOrder[this.battleState.currentTurnIndex];
    const currentGladiator = this.gameState.gladiators.get(currentGId);
    if (currentGladiator) {
      tickEffects(currentGladiator);
    }

    this.battleState.currentTurnIndex++;

    if (this.battleState.currentTurnIndex >= this.battleState.turnOrder.length) {
      this.battleState.currentTurnIndex = 0;
      this.battleState.round++;
    }

    this.checkBattleEnd();
  }

  private endTurn(): void {
    if (!this.battleState || !this.gameState) return;

    // Tick down effects for the current gladiator
    const currentGId = this.battleState.turnOrder[this.battleState.currentTurnIndex];
    const currentGladiator = this.gameState.gladiators.get(currentGId);
    if (currentGladiator) {
      tickEffects(currentGladiator);
    }

    // Reset targeting state and AI turn flag
    this.targetingMode = 'none';
    this.selectedSpell = null;
    this.validTargets = [];
    this.aiTurnInProgress = false;

    this.battleState.currentTurnIndex++;

    // Check for new round
    if (this.battleState.currentTurnIndex >= this.battleState.turnOrder.length) {
      this.battleState.currentTurnIndex = 0;
      this.battleState.round++;
      this.addMessage(`--- Kierros ${this.battleState.round} ---`);
    }

    this.checkBattleEnd();
  }

  private checkBattleEnd(): void {
    if (!this.battleState || !this.gameState) return;

    const bottomAlive = this.battleState.turnOrder.some(gId => {
      const g = this.gameState!.gladiators.get(gId);
      return g && isAlive(g) && g.ownerId === this.battleState!.bottomTeamId;
    });

    const topAlive = this.battleState.turnOrder.some(gId => {
      const g = this.gameState!.gladiators.get(gId);
      return g && isAlive(g) && g.ownerId === this.battleState!.topTeamId;
    });

    // Check win conditions
    if (!bottomAlive || !topAlive || this.battleState.round >= GAME_CONSTANTS.MAX_BATTLE_ROUNDS) {
      this.battleState.battleEnded = true;

      if (!topAlive && bottomAlive) {
        this.battleState.winningTeamId = this.battleState.bottomTeamId;
        this.addMessage('Voitit taistelun!');
      } else if (!bottomAlive && topAlive) {
        this.battleState.winningTeamId = this.battleState.topTeamId;
        this.addMessage('Hävisit taistelun!');
      } else {
        // Draw - random winner
        this.battleState.winningTeamId = random(2) === 0 ? this.battleState.bottomTeamId : this.battleState.topTeamId;
        this.addMessage('Taistelu päättyi tasapeliin - voittaja arvotaan.');
      }

      // Apply battle results (wins, losses, money)
      this.applyPlayerBattleResults();
    }
  }

  private applyPlayerBattleResults(): void {
    if (!this.battleState || !this.gameState) return;

    const winnerId = this.battleState.winningTeamId;
    const loserId = winnerId === this.battleState.bottomTeamId
      ? this.battleState.topTeamId
      : this.battleState.bottomTeamId;

    const winner = this.gameState.teams.get(winnerId!);
    const loser = this.gameState.teams.get(loserId);

    if (winner) {
      winner.wins++;
      const reward = 200 + random(100);
      modifyMoney(winner, reward);
      winner.rating = Math.min(100, winner.rating + 2);
      winner.arenaPoints += 10;
      this.addMessage(`${winner.name} saa ${reward} kr voittopalkinnon.`);
    }

    if (loser) {
      loser.losses++;
      loser.rating = Math.max(0, loser.rating - 1);
    }
  }

  private addMessage(msg: string): void {
    if (this.battleState) {
      this.battleState.messages.push(msg);

      // Limit buffer size
      if (this.battleState.messages.length > MESSAGE_LOG_MAX_BUFFER) {
        this.battleState.messages = this.battleState.messages.slice(-MESSAGE_LOG_MAX_BUFFER);
      }

      // Reset scroll to show newest messages
      this.messageScrollOffset = 0;

      console.log(`[Battle] ${msg}`);
    }
  }

  handleClick(x: number, y: number): void {
    console.log(`Battle click at (${x}, ${y})`);
  }

  handleKeyDown(key: string): void {
    if (key === 'Escape') {
      if (this.battleState?.battleEnded || this.spectatorMode) {
        // In spectator mode or exiting early, go to main menu
        // Otherwise, show battle summary then kill leaderboard
        if (this.spectatorMode || !this.battleState?.battleEnded) {
          this.context.onScreenChange(ScreenState.MAIN_MENU);
        } else {
          this.context.onScreenChange(ScreenState.BATTLE_SUMMARY);
        }
      }
      return;
    }

    // Don't process shortcuts if battle ended, not in a battle, or in spectator mode
    if (!this.battleState || this.battleState.battleEnded || !this.gameState || this.spectatorMode) return;

    const gladiator = this.getCurrentGladiator();
    if (!gladiator) return;

    // Check if it's player's turn
    const playerTeamId = this.battleState.bottomTeamId;
    if (gladiator.ownerId !== playerTeamId) return;

    // Movement keys (numpad style: 7=NW, 8=N, 9=NE, 4=W, 6=E, 1=SW, 2=S, 3=SE)
    const moveKeyMap: Record<string, string> = {
      '7': 'NW',
      '8': 'N',
      '9': 'NE',
      '4': 'W',
      '6': 'E',
      '1': 'SW',
      '2': 'S',
      '3': 'SE',
      // Arrow keys for cardinal directions
      'ArrowUp': 'N',
      'ArrowDown': 'S',
      'ArrowLeft': 'W',
      'ArrowRight': 'E',
    };

    const moveDir = moveKeyMap[key];
    if (moveDir && this.canMoveInDirection(gladiator, moveDir)) {
      this.moveGladiator(moveDir);
      return;
    }

    // Space = skip turn
    if (key === ' ' || key === 'Space') {
      this.endTurn();
      return;
    }

    // R = reload crossbow
    if (key === 'r' || key === 'R') {
      if (gladiator.needsReload) {
        this.reloadCrossbow(gladiator);
      }
      return;
    }

    // Q/W/E/S = cast spell 1-4
    const spellKeyMap: Record<string, number> = {
      'q': 0, 'Q': 0,
      'w': 1, 'W': 1,
      'e': 2, 'E': 2,
      's': 3, 'S': 3, // Note: 's' for spell 4 (not movement)
    };

    const spellIndex = spellKeyMap[key];
    if (spellIndex !== undefined) {
      const spells = getKnownSpells(gladiator);
      const spell = spells[spellIndex];
      if (spell && gladiator.mana >= spell.manaCost) {
        this.toggleSpellMode(gladiator, spell);
      }
      return;
    }

    // T = toggle ranged targeting mode
    if (key === 't' || key === 'T') {
      const rangedWpn = getRangedWeapon(gladiator);
      if (rangedWpn && gladiator.ammo > 0 && !gladiator.needsReload) {
        this.toggleRangedMode(gladiator);
      }
      return;
    }

    // A = autoplay round
    if (key === 'a' || key === 'A') {
      this.startAutoplay();
      return;
    }
  }
}
